import {
    LeadReportConnection,
    LeadReportResponseData,
    LeadReportSourceTypeItem,
} from "helpers/leads/generalReports";

import {
    ConnectionTableRow,
    DailyTimePoint,
    DatePreset,
    LeadGeneralReportData,
    LeadGeneralReportFilters,
    SourceBreakdownItem,
    SummaryMetrics,
} from "./LeadGeneralReportsPage.types";

export const MAX_DATE_RANGE_DAYS = 90;

// Format a Date as YYYY-MM-DD using LOCAL calendar fields. Do NOT use
// toISOString() here: it converts to UTC, so for users ahead of UTC (e.g.
// UTC+3) an early-morning "today" would resolve to yesterday and shift the
// whole preset range back by a day.
const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export const resolveDateRange = (
    filters: LeadGeneralReportFilters,
): { start: string; end: string } => {
    const today = new Date();

    if (filters.datePreset === "custom") {
        return {
            start: filters.startDate ?? formatLocalDate(today),
            end: filters.endDate ?? formatLocalDate(today),
        };
    }

    const daysMap: Record<Exclude<DatePreset, "custom">, number> = {
        last7: 6,
        last30: 29,
        last90: 89,
    };
    const start = new Date(today);
    start.setDate(today.getDate() - daysMap[filters.datePreset]);
    return { start: formatLocalDate(start), end: formatLocalDate(today) };
};

export interface RangeValidation {
    ok: boolean;
    code?: "RANGE_TOO_LONG" | "END_BEFORE_START";
    message?: string;
}

export const validateRange = (
    filters: LeadGeneralReportFilters,
): RangeValidation => {
    if (filters.datePreset !== "custom") return { ok: true };
    if (!filters.startDate || !filters.endDate) return { ok: true };

    const start = new Date(filters.startDate);
    const end = new Date(filters.endDate);

    if (end < start) {
        return {
            ok: false,
            code: "END_BEFORE_START",
            message: "End date cannot be earlier than start date.",
        };
    }

    const diffDays = Math.floor(
        (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
    );

    // A day-difference of exactly 90 (e.g. Jan 1 -> Mar 31) is a 90-day span
    // and must be allowed; only reject when it strictly exceeds the max.
    if (diffDays > MAX_DATE_RANGE_DAYS) {
        return {
            ok: false,
            code: "RANGE_TOO_LONG",
            message: "Date range cannot exceed 90 days.",
        };
    }

    return { ok: true };
};

const numberFormatter = new Intl.NumberFormat("en-US");
export const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value))
        return "-";
    return numberFormatter.format(value);
};

export const formatPct = (
    value: number | null | undefined,
    digits = 1,
): string => {
    if (value === null || value === undefined || Number.isNaN(value))
        return "-";
    return `${value.toFixed(digits)}%`;
};

const SOURCE_TYPE_META: Record<
    string,
    { name: string; category: string; accent: string }
> = {
    Facebook: {
        name: "Facebook",
        category: "Social Media",
        accent: "#1877F2",
    },
    "Facebook Lead": {
        name: "Facebook Ads",
        category: "Social Media",
        accent: "#1877F2",
    },
    TikTok: {
        name: "TikTok",
        category: "Video Social",
        accent: "#111827",
    },
    "Tiktok Lead Generation": {
        name: "TikTok Leads",
        category: "Video Social",
        accent: "#111827",
    },
    LinkedIn: {
        name: "LinkedIn",
        category: "Professional",
        accent: "#0A66C2",
    },
    "Linkedin Lead Generation": {
        name: "LinkedIn",
        category: "Professional",
        accent: "#0A66C2",
    },
    "Source API": {
        name: "Custom API",
        category: "Integration",
        accent: "#7C3AED",
    },
    Webhook: {
        name: "Webhook",
        category: "Raw Source",
        accent: "#0D9488",
    },
    Webform: {
        name: "Webform",
        category: "Raw Source",
        accent: "#0D9488",
    },
};

const FALLBACK_ACCENTS = [
    "#2563EB",
    "#7C3AED",
    "#0D9488",
    "#EA4335",
    "#F59E0B",
    "#0A66C2",
    "#111827",
];

const sourceMetaFor = (sourceType: string, fallbackIndex = 0) => {
    const meta = SOURCE_TYPE_META[sourceType];
    if (meta) return meta;
    return {
        name: sourceType || "Unknown",
        category: "Source",
        accent: FALLBACK_ACCENTS[fallbackIndex % FALLBACK_ACCENTS.length],
    };
};

const toDailyPoints = (
    rawDailyRows: {
        date: string;
        sent: number;
        failed: number;
        total: number;
    }[] = [],
): DailyTimePoint[] =>
    // API does not guarantee ordering; charts and the sparkline read
    // left->right as oldest->newest, so sort ascending by date first.
    // "YYYY-MM-DD" strings sort lexicographically == chronologically.
    [...rawDailyRows]
        .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
        .map((dailyRow) => {
            const sent = dailyRow.sent ?? 0;
            const failed = dailyRow.failed ?? 0;
            const total = dailyRow.total ?? sent + failed;
            // The endpoint's daily breakdown has no per-day "duplicated";
            // this remainder is really received/processed/pending leads.
            const other = Math.max(0, total - sent - failed);
            return { date: dailyRow.date, sent, failed, duplicated: other, total };
        });

const buildSummary = (
    raw: LeadReportResponseData["summary"],
): SummaryMetrics => {
    const totalLeads = raw?.total_leads ?? 0;
    const sent = raw?.total_sent ?? 0;
    const failed = raw?.total_failed ?? 0;
    const duplicated = raw?.total_duplicated ?? 0;

    const successDenominator = sent + failed || totalLeads || 1;
    const sentSuccessRate = (sent / successDenominator) * 100;
    const duplicatedRate = totalLeads ? (duplicated / totalLeads) * 100 : 0;
    const failureRate = totalLeads ? (failed / totalLeads) * 100 : 0;

    return {
        totalLeads,
        sent,
        failed,
        duplicated,
        sentSuccessRate,
        failedAnomalyPct: failureRate,
        duplicatedRate,
    };
};

const buildSourceBreakdown = (
    rawSourceItems: LeadReportSourceTypeItem[] = [],
): SourceBreakdownItem[] =>
    rawSourceItems.map((sourceItem, index) => {
        const meta = sourceMetaFor(sourceItem.source_type, index);
        return {
            sourceType: sourceItem.source_type,
            name: meta.name,
            category: meta.category,
            total: sourceItem.total ?? 0,
            sent: sourceItem.sent ?? 0,
            failed: sourceItem.failed ?? 0,
            accentColor: meta.accent,
        };
    });

const buildConnectionRows = (
    rawConnections: LeadReportConnection[] = [],
): ConnectionTableRow[] =>
    rawConnections.map((connection) => {
        const daily = toDailyPoints(connection.daily_breakdown);
        return {
            id: connection.connection_id,
            connectionName: connection.connection_name,
            sourceType: connection.source_type,
            destinationType: connection.destination_type,
            status: connection.status,
            total: connection.total_leads ?? 0,
            sent: connection.sent_count ?? 0,
            failed: connection.failed_count ?? 0,
            duplicated: connection.duplicated_count ?? 0,
            dailyBreakdown: daily,
        };
    });

export const parseLeadReport = (
    raw: LeadReportResponseData | null | undefined,
): LeadGeneralReportData | null => {
    if (!raw) return null;
    const daily = toDailyPoints(raw.daily_breakdown);
    return {
        summary: buildSummary(raw.summary),
        daily,
        sourceBreakdown: raw.source_type_breakdown
            ? buildSourceBreakdown(raw.source_type_breakdown)
            : undefined,
        connections: raw.connections
            ? buildConnectionRows(raw.connections)
            : undefined,
    };
};

export const downloadCsv = (
    rows: Array<Record<string, any>>,
    filename: string,
) => {
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const escapeCell = (value: any) => {
        const text = value === null || value === undefined ? "" : String(value);
        return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const csv = [
        headers.join(","),
        ...rows.map((row) =>
            headers.map((header) => escapeCell(row[header])).join(","),
        ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const downloadLink = document.createElement("a");
    downloadLink.href = url;
    downloadLink.download = filename;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(url);
};
